// frontend/src/components/LicenseKeyModal.js
import { trackSuccess } from "../utils/siteAnalytics";
import { useState } from "react";
import { ArrowRight, KeyRound, X } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const LICENSE_PURCHASE_URL = process.env.REACT_APP_LICENSE_PURCHASE_URL || "mailto:hello@arialabs.in?subject=Prime%20Portfolio%20license%20key";

// Safely extract a string error message from any Axios/FastAPI error response.
function apiErr(err, fallback = "Something went wrong. Please try again.") {
  const det = err?.response?.data?.detail;
  if (!det) return fallback;
  if (typeof det === "string") return det;
  if (Array.isArray(det)) return det.map((e) => e.msg || String(e)).join(". ");
  return fallback;
}

// Auto-uppercase and auto-insert dashes as the user types: PP-XXXX-XXXX-XXXX
function formatLicenseKey(raw) {
  let cleaned = (raw || "").toUpperCase().replace(/[^A-Z0-9]/g, "");
  if (cleaned.startsWith("PP")) cleaned = cleaned.slice(2);
  cleaned = cleaned.slice(0, 12);
  const groups = cleaned.match(/.{1,4}/g) || [];
  return groups.length ? `PP-${groups.join("-")}` : "PP-";
}

export default function LicenseKeyModal({ onClose, onSuccess, title = "Psychometric Career Interview" }) {
  const auth = useAuth();
  const [licenseKey, setLicenseKey] = useState("PP-");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const submit = async (event) => {
    event.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      const { data } = await auth.client.post("/interviews", { title, license_key: licenseKey });
      trackSuccess("interview_started", data.id);
      onSuccess(data);
    } catch (err) {
      setError(apiErr(err, "Could not start interview — please try again."));
      setSubmitting(false);
    }
  };

  return (
    <div
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: "1rem" }}
      data-testid="license-key-modal"
    >
      <div style={{ background: "var(--bg)", borderRadius: "1rem", padding: "2rem", maxWidth: "440px", width: "100%", boxShadow: "0 25px 50px rgba(0,0,0,.25)", position: "relative" }}>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          data-testid="license-key-modal-close"
          style={{ position: "absolute", top: "1rem", right: "1rem", background: "none", border: "none", cursor: "pointer", color: "var(--text)" }}
        >
          <X size={20} />
        </button>
        <h3 style={{ margin: "0 0 .5rem", fontSize: "1.1rem", color: "var(--text)" }}>
          <KeyRound size={18} style={{ verticalAlign: "-3px", marginRight: ".4rem" }} />
          Enter your license key
        </h3>
        <p style={{ margin: "0 0 1.25rem", fontSize: ".85rem", opacity: .7, color: "var(--text)" }}>
          Each license key starts one interview. Keys are provided with your purchase.
        </p>
        <div className="license-key-help" data-testid="license-key-help">
          <a href={LICENSE_PURCHASE_URL}>Get a license key</a>
          <p><strong>Already purchased?</strong> Check your purchase email for a key beginning with PP-. If it is missing or rejected, contact us through the same link.</p>
        </div>
        <form onSubmit={submit} className="stack" data-testid="license-key-form" style={{ marginTop: 0 }}>
          <label data-testid="license-key-label">License key
            <input
              data-testid="license-key-input"
              value={licenseKey}
              onChange={(e) => setLicenseKey(formatLicenseKey(e.target.value))}
              placeholder="PP-XXXX-XXXX-XXXX"
              maxLength={17}
              autoFocus
              required
            />
          </label>
          {error && <div className="error-note" data-testid="license-key-error">{error}</div>}
          <button
            className="primary-action"
            type="submit"
            disabled={submitting || licenseKey.length < 17}
            data-testid="license-key-submit"
          >
            {submitting ? "Verifying…" : "Start interview"} <ArrowRight size={18} />
          </button>
        </form>
      </div>
    </div>
  );
}
